/**
 * First-paint asset set: editor-visible + not hidden by the current rule actions.
 * Prefetching every scene GLB / library map used to compete with the default view.
 */

import {
  buildEffectiveVisibleMap,
  collectGroupMaterialReceiverIds,
  type SceneObjectLike,
} from '@config3d/scene';

export interface FirstWaveRuleAction {
  type: string;
  targetId?: string;
  value?: unknown;
}

export interface FirstWaveSceneObject extends SceneObjectLike {
  name: string;
  type?: string;
  asset?: { url?: string } | null;
  tags?: string[];
  metadata?: {
    parentId?: string | null;
    tags?: string[];
  };
}

function parseVisibilityRefs(raw: unknown): Array<{ kind: 'scene' | 'tag'; id: string }> {
  if (!Array.isArray(raw)) return [];
  const out: Array<{ kind: 'scene' | 'tag'; id: string }> = [];
  for (const x of raw) {
    if (x == null || typeof x !== 'object') continue;
    const k = (x as { kind?: string }).kind;
    const id = (x as { id?: string }).id;
    if ((k === 'scene' || k === 'tag') && typeof id === 'string' && id.length > 0) {
      out.push({ kind: k, id });
    }
  }
  return out;
}

export function hiddenSetsFromRuleActions(actions: FirstWaveRuleAction[] | null | undefined): {
  hidden: Set<string>;
  hiddenTags: Set<string>;
} {
  const hidden = new Set<string>();
  const shown = new Set<string>();
  const hiddenTags = new Set<string>();
  const shownTags = new Set<string>();

  for (const action of actions ?? []) {
    if (action.type === 'set-visibility' && action.value != null && typeof action.value === 'object') {
      const v = action.value as { show?: unknown; hide?: unknown };
      for (const t of parseVisibilityRefs(v.hide)) {
        if (t.kind === 'tag') hiddenTags.add(t.id);
        else hidden.add(t.id);
      }
      for (const t of parseVisibilityRefs(v.show)) {
        if (t.kind === 'tag') shownTags.add(t.id);
        else shown.add(t.id);
      }
      continue;
    }
    if (action.type === 'hide-object' || action.type === 'hide-component') {
      if (action.targetId) hidden.add(action.targetId);
    }
    if (action.type === 'show-object' || action.type === 'show-component') {
      if (action.targetId) shown.add(action.targetId);
    }
    if (action.type === 'hide-tagged' && action.targetId) hiddenTags.add(action.targetId);
    if (action.type === 'show-tagged' && action.targetId) shownTags.add(action.targetId);
  }
  for (const id of shown) hidden.delete(id);
  for (const tag of shownTags) hiddenTags.delete(tag);
  return { hidden, hiddenTags };
}

function objectTags(o: FirstWaveSceneObject): string[] {
  const m = o.metadata?.tags ?? [];
  const t = o.tags ?? [];
  return [...m, ...t];
}

function ruleVisible(
  obj: FirstWaveSceneObject,
  hidden: Set<string>,
  hiddenTags: Set<string>,
  byId: Map<string, FirstWaveSceneObject>,
): boolean {
  if (hidden.has(obj.id) || hidden.has(obj.name)) return false;
  if (objectTags(obj).some((tag) => hiddenTags.has(tag))) return false;
  const visited = new Set<string>();
  let pid: string | null | undefined = obj.parentId ?? obj.metadata?.parentId ?? null;
  while (pid) {
    if (visited.has(pid)) break;
    visited.add(pid);
    const p = byId.get(pid);
    if (!p) break;
    if (hidden.has(p.id) || hidden.has(p.name)) return false;
    if (objectTags(p).some((tag) => hiddenTags.has(tag))) return false;
    pid = p.parentId ?? p.metadata?.parentId ?? null;
  }
  return true;
}

export function firstWaveObjectIds(
  objects: FirstWaveSceneObject[],
  actions: FirstWaveRuleAction[] | null | undefined,
): Set<string> {
  const { hidden, hiddenTags } = hiddenSetsFromRuleActions(actions);
  const byId = new Map(objects.map((o) => [o.id, o]));
  const editorVisible = buildEffectiveVisibleMap(objects);
  const ids = new Set<string>();
  for (const o of objects) {
    if (editorVisible.get(o.id) === false) continue;
    if (!ruleVisible(o, hidden, hiddenTags, byId)) continue;
    ids.add(o.id);
  }
  return ids;
}

export function firstWaveGlbUrls(
  objects: FirstWaveSceneObject[],
  actions: FirstWaveRuleAction[] | null | undefined,
): string[] {
  const ids = firstWaveObjectIds(objects, actions);
  const urls: string[] = [];
  const seen = new Set<string>();
  for (const o of objects) {
    if (!ids.has(o.id)) continue;
    const url = o.asset?.url;
    if (typeof url !== 'string' || url.length === 0 || seen.has(url)) continue;
    seen.add(url);
    urls.push(url);
  }
  return urls;
}

type AssignmentTarget = {
  type?: string;
  objectId?: string;
  meshName?: string;
  tagId?: string;
  groupObjectId?: string;
};

function assignmentTouchesFirstWave(
  objects: FirstWaveSceneObject[],
  firstWaveIds: Set<string>,
  targets: AssignmentTarget[] | undefined,
): boolean {
  if (!targets?.length) return false;
  for (const target of targets) {
    if (target.type === 'object' && target.objectId && firstWaveIds.has(target.objectId)) return true;
    if (target.type === 'mesh' && target.objectId && firstWaveIds.has(target.objectId)) return true;
    if (target.type === 'group' && target.groupObjectId) {
      for (const oid of collectGroupMaterialReceiverIds(objects, target.groupObjectId)) {
        if (firstWaveIds.has(oid)) return true;
      }
    }
    if (target.type === 'tag' && target.tagId) {
      for (const o of objects) {
        if (!firstWaveIds.has(o.id)) continue;
        if (objectTags(o).includes(target.tagId)) return true;
      }
    }
  }
  return false;
}

export function firstWaveMaterialIds(
  objects: FirstWaveSceneObject[],
  actions: FirstWaveRuleAction[] | null | undefined,
  assignments:
    | Array<{ materialId?: string; targets?: AssignmentTarget[] }>
    | null
    | undefined,
): Set<string> {
  const firstWaveIds = firstWaveObjectIds(objects, actions);
  const ids = new Set<string>();

  for (const assignment of assignments ?? []) {
    if (!assignment.materialId) continue;
    if (assignmentTouchesFirstWave(objects, firstWaveIds, assignment.targets)) {
      ids.add(assignment.materialId);
    }
  }

  for (const action of actions ?? []) {
    if (action.type !== 'apply-material' || !action.targetId || action.value == null) continue;
    const tid = action.targetId;
    const receiverIds = new Set<string>();
    if (tid.startsWith('tag:')) {
      const tagId = tid.slice(4);
      for (const o of objects) {
        if (objectTags(o).includes(tagId)) receiverIds.add(o.id);
      }
    } else {
      for (const t of objects.filter((o) => o.id === tid || o.name === tid)) {
        if (t.type === 'group') {
          for (const oid of collectGroupMaterialReceiverIds(objects, t.id)) receiverIds.add(oid);
        } else {
          receiverIds.add(t.id);
        }
      }
    }
    for (const id of receiverIds) {
      if (firstWaveIds.has(id)) {
        ids.add(String(action.value));
        break;
      }
    }
  }

  return ids;
}
